/**
 * QMP-FE-5 (F1) — STEP 6 "REVIEW & PUBLISH", THE PURE PART. No React, no api.
 * Fed with `GET /qmp` (`structure`, `packages`) and the project's companies,
 * it answers what the review shows.
 *
 * WHAT THE SHEET SHOWS IS WHAT THE REVISION WOULD FREEZE: per section, the
 * package's saved draft, else what the current revision froze for it. Never
 * `proposed` and never the standard rule — the declare reads the drafts only,
 * and a section nobody saved is a blocker the server names (RULES_INCOMPLETE),
 * not a value. Such a row shows "—".
 *
 * THE GROUPS (mockup v79 `renderReview`, lines 1305-1316): packages with the
 * SAME rules in every section — compared by canonical form, parties never part
 * of the key — in package order, first seen first. The sheet is the first
 * package's (`ruleSheet(g.items[0])`); each package's parties are listed under
 * it. The labels and the grammar of the sheet are `sheetRows` (v79:1347-1366).
 *
 * A sheet value is a list of lines; a line is a list of parts:
 * `{t}` text, `{b}` bold, `{sep: true}` the "·" separator, `{dim}` dim text.
 */
import {
  canonical, formatNotice, formatSignoffDue, formatDocRelease, formatResponseTime, formatAi,
  withReceiverLimit, receiverLimitOf, formatReceiverLimit, RECEIVER_LIMIT_LABEL,
} from './qmpRules';

export const REVIEW_SECTIONS = [
  'itp_rules', 'hold_points', 'witness_points', 'handover', 'punch_list',
  'ncr', 'rfi', 'visibility', 'ai',
];

const has = (o, k) => Boolean(o) && Object.prototype.hasOwnProperty.call(o, k);

/** `{set: true, value}` — saved draft ?? current revision — or `{set: false}`.
 *  A saved or frozen `null` is a value (RFIs in internal QA, visibility
 *  without three parties). A handover rule saved before §19 reads "No time
 *  limit" (QMP-FE-7), so it groups with the same rule saved today. */
export function sheetValue(section, entry) {
  const read = (v) => (section === 'handover' ? withReceiverLimit(v) : v);
  const draft = entry && entry.draft;
  if (draft && (draft.sections_saved || []).includes(section) && has(draft, section)) {
    return { set: true, value: read(draft[section]) };
  }
  const rules = entry && entry.current && entry.current.rules;
  if (rules && has(rules, section)) return { set: true, value: read(rules[section]) };
  return { set: false };
}

export const packageRules = (entry) => {
  const out = {};
  REVIEW_SECTIONS.forEach((s) => { out[s] = sheetValue(s, entry); });
  return out;
};

// ---------------------------------------------------------------------------
// groups
// ---------------------------------------------------------------------------

export const GROUP_ALL = 'All packages';
export const groupTitle = (n, total, firstName) => {
  if (n === total) return GROUP_ALL;
  return n > 1 ? `${n} packages · same rules` : firstName;
};

/** `[{key, rows, rules, title, names}]` — `names` only when 1 < n < total. */
export function reviewGroups(structure, packages) {
  const groups = [];
  (structure || []).forEach((row) => {
    const rules = packageRules(packages && packages[row.qwp_id]);
    const key = canonical(REVIEW_SECTIONS.map((s) => rules[s]));
    const g = groups.find((x) => x.key === key);
    if (g) g.rows.push(row);
    else groups.push({ key, rows: [row], rules });
  });
  const total = (structure || []).length;
  return groups.map((g) => ({
    ...g,
    title: groupTitle(g.rows.length, total, g.rows[0].name),
    names: g.rows.length > 1 && g.rows.length < total ? g.rows.map((r) => r.name).join(' · ') : null,
  }));
}

// ---------------------------------------------------------------------------
// names
// ---------------------------------------------------------------------------

const POSITIONS = ['execution', 'verification', 'acceptance'];
const PARTY_KEY = { execution: 'executing', verification: 'verification', acceptance: 'acceptance' };
const DUE_KEY = { execution: 'executing', verification: 'verification', acceptance: 'acceptance' };

const hasPos = (row, p) => (p === 'execution' ? true : Boolean(row && row[p]));
const isInternal = (row) => !hasPos(row, 'verification') && !hasPos(row, 'acceptance');
const isFull = (row) => hasPos(row, 'verification') && hasPos(row, 'acceptance');

/** Mockup `nmCo(posCo(q, p))`: the company, "To be appointed", or "—". */
export function partyName(row, p) {
  const party = row && row[PARTY_KEY[p]];
  if (p === 'execution') {
    if (!party || party.appointed === false) return 'To be appointed';
    return party.name || '—';
  }
  return party ? party.name || '—' : '—';
}

/** Mockup `resolve(q, v)` for a party reference of the rules. */
function refName(row, ref, companyNames) {
  if (ref && ref.position) {
    if (!hasPos(row, ref.position)) return null;
    if (ref.position === 'execution') {
      const ep = row && row.executing;
      return !ep || ep.appointed === false ? 'a company to be appointed' : ep.name;
    }
    return row[ref.position].name;
  }
  if (ref && ref.company_id) return (companyNames && companyNames[ref.company_id]) || '—';
  return null;
}
const refNames = (row, refs, companyNames) => (refs || [])
  .map((r) => refName(row, r, companyNames)).filter(Boolean);

// ---------------------------------------------------------------------------
// the sheet (v79 `sheetRows`)
// ---------------------------------------------------------------------------

const T = (t) => ({ t });
const B = (b) => ({ b });
const SEP = { sep: true };
const DIM = (dim) => ({ dim });
const UNSET = [[T('—')]];

const UNIT_LABEL = { asset: 'Asset by asset', package: 'Whole package' };
const CATEGORY_LABEL = { B: 'Category B', C: 'Category C' };

/** The handover stages of a path (v79 `stages`). */
function stagesOf(path) {
  if (path === 'EVA') return [['execution', 'verification'], ['verification', 'acceptance']];
  if (path === 'EA') return [['execution', 'acceptance']];
  if (path === 'EV') return [['execution', 'verification']];
  return [];
}

const due = (dues, key) => formatResponseTime(dues ? dues[key] : null);

function itpLine(row, v, companyNames) {
  const line = [T('Prepared by '), B(refNames(row, v.prepared_by, companyNames).join(' or ') || '—')];
  const e = v.endorsement || {};
  if (e.required) {
    line.push(SEP, T('Endorsed by '), B(refNames(row, e.endorsed_by, companyNames).join(' + ') || '—'),
      SEP, B(formatResponseTime(e.response_time)));
  } else {
    line.push(SEP, T('No endorsement required'));
  }
  return [line];
}

function holdLine(v) {
  const doc = formatDocRelease(v);
  return [[
    T('Booking notice '), B(formatNotice(v.notice)),
    SEP, T('Sign-off '), B(formatSignoffDue(v.signoff_due)), T(' the inspection'),
    SEP, T('Document release '), B(doc === 'Set in each ITP' ? 'set in each ITP' : doc),
  ]];
}

function witnessLine(v) {
  const s = v.signoff_due && v.signoff_due.at === 'end_of_working_day'
    ? 'end of the working day' : formatSignoffDue(v.signoff_due);
  return [[
    T('Notice '), B(formatNotice(v.notice)), T(' the window opens'),
    SEP, T('Sign-off '), B(s),
  ]];
}

function handoverLines(row, v) {
  const stages = stagesOf(v && v.path);
  if (!stages.length) return [[T('No handover '), DIM('internal QA')]];
  return stages.map(([from, to], i) => {
    const h = (v.handovers || [])[i] || {};
    return [
      B(partyName(row, from)), T(' → '), B(partyName(row, to)),
      SEP, T(UNIT_LABEL[h.unit] || '—'), SEP, T(formatNotice(h.notice)),
    ];
  });
}

const plText = (h) => {
  const cats = (h && h.open_categories) || [];
  return h && h.qa_complete_with_open_items && cats.length
    ? [B(cats.map((c) => CATEGORY_LABEL[c] || c).join(' and ')), T(' items may stay open at QA Complete')]
    : [T('Every item closed before QA Complete')];
};

function punchLines(row, v, handover) {
  const hs = v.handovers || [];
  if (v.per_handover && handover && handover.path === 'EVA') {
    const E = partyName(row, 'execution');
    const V = partyName(row, 'verification');
    const A = partyName(row, 'acceptance');
    return [[T(`${E} → ${V}: `), ...plText(hs[0])], [T(`${V} → ${A}: `), ...plText(hs[1])]];
  }
  return [plText(hs[0])];
}

const partsOf = (row) => POSITIONS.filter((p) => hasPos(row, p));

function ncrLines(row, v) {
  const parts = partsOf(row);
  if (v.per_company && parts.length > 1) {
    return parts.map((p) => [
      B(partyName(row, p)), T(': response '),
      p === 'acceptance' ? T('N/A') : B(due(v.response_due, DUE_KEY[p])),
      SEP, T('decision '), B(due(v.decision_due, DUE_KEY[p])),
    ]);
  }
  return [[T('Response '), B(due(v.response_due, 'all')), SEP, T('Decision '), B(due(v.decision_due, 'all'))]];
}

function rfiLines(row, v) {
  if (isInternal(row) || v == null) return [[T('No other company to ask')]];
  const parts = partsOf(row);
  if (v.per_company && parts.length > 1) {
    return parts.map((p) => [B(partyName(row, p)), T(' answers in '), B(due(v.answer_due, DUE_KEY[p]))]);
  }
  return [[T('Along the chain'), SEP, T('Answered in '), B(due(v.answer_due, 'all'))]];
}

const yn = (b) => (b ? 'yes' : 'no');
function visibilityLines(row, v) {
  const E = partyName(row, 'execution');
  const V = partyName(row, 'verification');
  const A = partyName(row, 'acceptance');
  return [
    [B(A), T(' sees NCRs between the other two: '), B(yn(v.acceptance_sees_ev_ncrs)),
      SEP, T('RFIs: '), B(yn(v.acceptance_sees_ev_rfis))],
    [B(E), T(` sees NCRs between ${V} and ${A}: `), B(yn(v.executing_sees_va_ncrs)),
      SEP, T('RFIs: '), B(yn(v.executing_sees_va_rfis))],
  ];
}

function partiesLine(row) {
  if (isInternal(row)) return [[B(partyName(row, 'execution')), T(' '), DIM('internal QA')]];
  const line = [];
  partsOf(row).forEach((p, i) => {
    if (i) line.push(T(' → '));
    line.push(B(partyName(row, p)));
  });
  return [line];
}

/** `[{label, lines}]` — the sheet of one package (`row`, its `rules` from
 *  `packageRules`). `companyNames` = `{participation_id: name}`. */
export function sheetRows(row, rules, companyNames) {
  const val = (s, fn) => (rules[s] && rules[s].set && rules[s].value != null ? fn(rules[s].value) : UNSET);
  const rows = [];
  rows.push({ label: 'Parties', lines: partiesLine(row) });
  rows.push({ label: 'ITPs', lines: val('itp_rules', (v) => itpLine(row, v, companyNames)) });
  rows.push({ label: 'Hold points', lines: val('hold_points', holdLine) });
  rows.push({ label: 'Witness points', lines: val('witness_points', witnessLine) });
  rows.push({ label: 'Handover', lines: val('handover', (v) => handoverLines(row, v)) });
  const handover = rules.handover && rules.handover.set ? rules.handover.value : null;
  // §19 (QMP-FE-7) — the receiving company's time to sign, wherever there is
  // a handover (internal QA has no receiving company).
  if (!handover || (handover.path || 'none') !== 'none') {
    rows.push({
      label: RECEIVER_LIMIT_LABEL,
      lines: val('handover', (v) => [[B(formatReceiverLimit(receiverLimitOf(v)))]]),
    });
  }
  rows.push({ label: 'Punch list', lines: val('punch_list', (v) => punchLines(row, v, handover)) });
  rows.push({ label: 'NCRs', lines: val('ncr', (v) => ncrLines(row, v)) });
  const rfi = rules.rfi || { set: false };
  rows.push({
    label: 'RFIs',
    lines: rfi.set || isInternal(row) ? rfiLines(row, rfi.set ? rfi.value : null) : UNSET,
  });
  if (isFull(row)) rows.push({ label: 'Visibility', lines: val('visibility', (v) => visibilityLines(row, v)) });
  rows.push({ label: 'AI drafting', lines: val('ai', (v) => [[B(formatAi(v))]]) });
  return rows;
}

/** The `.pp` line of one package (v79 `party`, line 1311). */
export function partyLine(row, rules, companyNames) {
  const ep = row.executing && row.executing.appointed !== false ? row.executing.name : 'to be appointed';
  const vp = hasPos(row, 'verification') ? row.verification.name : 'none';
  const ap = hasPos(row, 'acceptance') ? row.acceptance.name : 'none';
  const itp = rules.itp_rules && rules.itp_rules.set ? rules.itp_rules.value : null;
  const by = itp ? refNames(row, itp.prepared_by, companyNames).join(' or ') || '—' : '—';
  const endorsed = itp && itp.endorsement && itp.endorsement.required
    ? ` · endorsed by ${refNames(row, itp.endorsement.endorsed_by, companyNames).join(' + ') || '—'}` : '';
  return `${row.name}: Executing ${ep} · Verification ${vp} · Acceptance ${ap}`
    + `${isInternal(row) ? ' · internal QA' : ''} · ITP prepared by ${by}${endorsed}`;
}

/** v79 `co.size`: the distinct companies named in the packages' parties. */
export function namedCompanyCount(structure) {
  const ids = new Set();
  (structure || []).forEach((row) => {
    ['executing', 'verification', 'acceptance'].forEach((k) => {
      const p = row[k];
      if (p && p.participation_id && p.appointed !== false) ids.add(p.participation_id);
    });
  });
  return ids.size;
}

/** D32 (owner 136 A) — the parties the revision in force froze for a package
 *  (`packages[q].current.parties`: `{execution: {company_id,
 *  name_at_publication, appointed}, verification, acceptance}`), as a row of
 *  `structure[]`, so `partyLine` prints them and no second renderer exists.
 *  The names are the ones published. `null` when the revision froze no parties
 *  for the package (no `current`, or no `execution` entry in its parties). */
export function frozenPartyRow(row, entry) {
  const parties = entry && entry.current && entry.current.parties;
  const ex = parties && parties.execution;
  if (!ex || typeof ex !== 'object') return null;
  const frozen = (p) => (p ? { participation_id: p.company_id || null, name: p.name_at_publication } : null);
  return {
    ...row,
    executing: {
      participation_id: ex.appointed === false ? null : ex.company_id || null,
      name: ex.name_at_publication,
      appointed: ex.appointed !== false,
    },
    verification: frozen(parties.verification),
    acceptance: frozen(parties.acceptance),
  };
}

const positionId = (party) => (party && party.participation_id) || null;
const executingId = (row) => (row && row.executing && row.executing.appointed !== false
  ? positionId(row.executing) : null);

/** D32 (owner 136 A) — the frozen row (`frozenPartyRow`) when a position of the
 *  package holds another company than the revision in force froze, else `null`.
 *  By `participation_id` in the three positions, never by name: renaming a
 *  company is not a change (the server's rule, `qmp_plan_unchanged`). An
 *  Executing Party still to be appointed is no company, so appointing one is a
 *  change ("to be appointed" struck through, the company beside it). */
export function partyChange(row, entry) {
  const was = frozenPartyRow(row, entry);
  if (!was) return null;
  const changed = executingId(row) !== executingId(was)
    || positionId(row && row.verification) !== positionId(was.verification)
    || positionId(row && row.acceptance) !== positionId(was.acceptance);
  return changed ? was : null;
}

/** D32 (owner 132 A) — what "Who will be asked to approve" says of an Executing
 *  Party still to be appointed. The owner's word, in that block only: the rest
 *  of the step says "To be appointed" until the copy pass of the v82. */
export const NOT_ASSIGNED = 'Not assigned';

/** `roles[]` of `approvers_if_published` (the server's `QMP_APPROVAL_ROLES`) -> words. */
const APPROVER_ROLE_LABEL = {
  execution: 'Executing',
  verification: 'Verification',
  acceptance: 'Acceptance',
  itp_author: 'ITP author',
  endorsing: 'Endorsing',
};

const approverText = (a) => {
  const roles = (a.roles || []).map((r) => APPROVER_ROLE_LABEL[r] || r).join(', ');
  const to = a.nominee && a.nominee.name ? ` → ${a.nominee.name}` : '';
  return `${a.name || '—'}${roles ? ` (${roles})` : ''}${to}`;
};

/** D32 (owner 132 A) — "Who will be asked to approve": `[{qwpId, name, text}]`,
 *  one per package of `structure[]`. `text` says what the server forecasts
 *  (`packages[q].approvers_if_published`, D31): each company with its roles
 *  and, where the request would reach a person, "→ <name>", joined by " · ".
 *  An Executing Party still to be appointed is no company (the server invents
 *  none) and leads the line as "Executing: Not assigned". Nothing at all: "—". */
export function approverLines(structure, packages) {
  return (structure || []).map((row) => {
    const entry = packages && packages[row.qwp_id];
    const parts = ((entry && entry.approvers_if_published) || []).filter(Boolean).map(approverText);
    if (row.executing && row.executing.appointed === false) parts.unshift(`Executing: ${NOT_ASSIGNED}`);
    return { qwpId: row.qwp_id, name: row.name, text: parts.length ? parts.join(' · ') : '—' };
  });
}

// ---------------------------------------------------------------------------
// blockers — the server decided them; the screen only says them and points
// at the step that resolves each (copy new, to be reviewed by the owner)
// ---------------------------------------------------------------------------

/** Section -> `{step, sub, title}` of the wizard. QMP-FE-8b — `step` is the
 *  v83 page (`PAGE_OF`, l. 1534). QMP-FE-8d — the titles are the v83 ones the
 *  server serves (`standard_steps[].title`, D37): `describeBlocker` reads the
 *  served titles when its caller passes them; this copy stays for a caller
 *  that has no `standard_steps` in hand (`PublishStep`, which 8d does not
 *  touch). */
export const SECTION_PLACE = {
  itp_rules: { step: 2, sub: 'itp_rules', title: 'ITP preparation and endorsement' },
  hold_points: { step: 2, sub: 'hold_points', title: 'Hold points' },
  witness_points: { step: 2, sub: 'witness_points', title: 'Witness points' },
  handover: { step: 4, sub: 'handover', title: 'Handover' },
  punch_list: { step: 4, sub: 'punch_list', title: 'Punch list & QA Complete' },
  ncr: { step: 3, sub: 'ncr', title: 'Non-conformances · NCRs' },
  rfi: { step: 3, sub: 'rfi', title: 'Requests for information · RFIs' },
  visibility: { step: 3, sub: 'visibility', title: 'Visibility of NCRs and RFIs' },
  ai: { step: 3, sub: 'ai', title: 'AI-assisted drafting' },
};

/** The place of a section, titled by the served `standard_steps` when given. */
const placeOf = (section, served) => {
  const p = SECTION_PLACE[section];
  if (!p) return undefined;
  const hit = (served || []).find((x) => x && x.section === section && x.title);
  return hit ? { ...p, title: hit.title } : p;
};

const AUTHORITY_TEXT = {
  NOT_GOVERNING_COMPANY: 'Only a Quality Management Plan Signatory of the company that governs this project can publish its QMP.',
  NOT_QMP_SIGNATORY: 'Publishing the QMP is a signature: you need to be a Quality Management Plan Signatory for your company on this project.',
};
export const AUTHORITY_CODES = Object.keys(AUTHORITY_TEXT);

const joinAnd = (xs) => (xs.length > 1 ? `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}` : xs[0] || '');

/** `{code, qwpId, text, places: [{step, sub, title}]}` for one server blocker.
 *  `served` (optional) = `GET /qmp` `standard_steps`, whose titles win. */
export function describeBlocker(blocker, structure, served) {
  const code = blocker && blocker.code;
  const pkg = (structure || []).find((r) => r.qwp_id === (blocker && blocker.qwp_id));
  const name = pkg ? pkg.name : 'A package';
  if (AUTHORITY_TEXT[code]) return { code, qwpId: null, text: AUTHORITY_TEXT[code], places: [] };
  if (code === 'NOTHING_TO_PUBLISH') {
    return {
      code, qwpId: null, text: 'There is nothing to publish yet: no rule has been saved.', places: [placeOf('itp_rules', served)],
    };
  }
  if (code === 'NEEDS_REVIEW') {
    const place = placeOf(blocker.section, served);
    return {
      code,
      qwpId: blocker.qwp_id,
      text: `${name}: ${place ? place.title : blocker.section} needs review — the package's parties changed after the rule was saved.`,
      places: place ? [place] : [],
    };
  }
  if (code === 'RULES_INCOMPLETE') {
    // One way to each step/subarea: handover and punch list share step 4.
    const places = [];
    (blocker.sections || []).map((s) => placeOf(s, served)).filter(Boolean).forEach((p) => {
      if (!places.some((x) => x.step === p.step && x.sub === p.sub)) places.push(p);
    });
    const titles = (blocker.sections || []).map((s) => (placeOf(s, served) ? placeOf(s, served).title : s));
    return {
      code,
      qwpId: blocker.qwp_id,
      text: `${name}: ${joinAnd(titles)} ${titles.length > 1 ? 'are' : 'is'} not saved.`,
      places,
    };
  }
  if (code === 'NOTHING_CHANGED') {
    // D32 (owner 135 B) — the sentence of the server's own 422 on the declare.
    return {
      code,
      qwpId: null,
      text: `Nothing has changed since Revision ${blocker.revision}: there is no new revision to publish.`,
      places: [],
    };
  }
  return { code, qwpId: null, text: `This revision cannot be published yet (${code}).`, places: [] };
}
